/**
 * Global demo-only signup interceptor.
 * Any anchor/Link clicked that points to /signup will open the demo modal
 * instead of navigating, without needing to edit each link individually.
 */
import { useEffect, useState, useCallback } from "react";
import { DemoOnlyModal } from "./DemoOnlyModal";

export function DemoSignupInterceptor(): JSX.Element {
  const [open, setOpen] = useState(false);

  const onClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handler = (e: MouseEvent) => {
      // Only handle plain left-clicks without modifier keys
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      const isBlocked = (path: string) =>
        href === path || href.startsWith(`${path}?`) || href.startsWith(`${path}#`);
      // Block CTAs to login or signup in demo mode
      if (isBlocked("/signup") || isBlocked("/login")) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(true);
      }
    };

    // Capture phase so we beat React Router's own click handler
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return <DemoOnlyModal isOpen={open} onClose={onClose} />;
}
